/**
 * ทะลวงขั้น (data/breakthrough.ts): stage 9 of a realm holds. Cultivation still accrues, but past the stage-9 line it is
 * banked (`breakthrough.bank`) and the rank stays; `breakthroughUse` spends a pill's say (opens the trial), the guardian
 * is the next realm's sparring partner fought through the duelling ground's challenge (systems/duelGround.ts: duel rules,
 * the sparrer brain, wind-ups). A win lifts the hold and pays the bank and consumes the pill; a loss shuts the trial for
 * `retrySeconds` and keeps the pill. Time only from `step(dt)`; no randomness.
 */
import { MAX_RANK, rankFor, rankThreshold, REALMS, STAGES } from '../cultivation';
import { BREAKTHROUGH, newBreakthrough, pillOf, type BreakthroughState } from '../data/breakthrough';
import type { Duel } from '../world';
import type { PlayerCommand } from '../world';
import type { SystemHost, WorldSystem } from './types';

export type BreakthroughCommand = { t: 'breakthroughUse' };

export const HELD_NOTICE = 'ต้องทะลวงขั้น — ใช้ยาทะลวงขั้น';

export class BreakthroughSystem implements WorldSystem {
  readonly name = 'breakthrough';
  private told = false;
  private seen: Duel | null = null;

  constructor(private readonly h: SystemHost) {}

  /** The gate's state; kept off the save (a transient copy) until the first hold, so a character never held saves nothing. */
  private cur: BreakthroughState | null = null;
  private get st(): BreakthroughState {
    const pr = this.h.world.progress;
    if (pr.breakthrough) return (this.cur = pr.breakthrough);
    // A character already past a realm (an old save) has passed it; set once, never raised by a jump later.
    this.cur ??= { ...newBreakthrough(), passed: Math.floor(rankFor(pr.cultivationSpent).index / STAGES) };
    return this.cur;
  }

  /** Whether the player is held at stage 9 (a realm with a realm after it). */
  held(): boolean {
    const pr = this.h.world.progress;
    const k = rankFor(pr.cultivationSpent).index;
    return k < MAX_RANK && k % STAGES === STAGES - 1 && Math.floor(k / STAGES) >= this.st.passed;
  }

  /** The sparring tier of the guardian of the hold in force. */
  guardianTier(): number {
    return this.st.passed + 1;
  }

  /** The pill of the hold in force (good id), or null. */
  pill(): string | null {
    return pillOf(this.st.passed);
  }

  setup(): void {
    void this.st;
  }

  /** The state goes into the save once it matters (held, or open, or a cool-down). */
  private persist(): void {
    const pr = this.h.world.progress;
    if (!pr.breakthrough && this.cur && (this.cur.bank > 0 || this.cur.open || this.cur.cool > 0 || this.cur.passed > 0 || this.held()))
      pr.breakthrough = this.cur;
  }

  readonly commands = {
    breakthroughUse: () => this.use(),
  };

  private use(): void {
    const w = this.h.world;
    const s = this.st;
    if (!this.held()) return this.h.notice('ยังไม่ถึงชั้น 9 ของขั้นนี้');
    if (s.open) return this.h.notice('เปิดด่านแล้ว — ท้าผู้พิทักษ์ที่ลานประลอง');
    const id = this.pill();
    if (!id || (w.progress.trades.goods[id] ?? 0) < 1) return this.h.notice('ไม่มียาทะลวงขั้นของขั้นนี้');
    s.open = true;
    this.persist();
    this.h.notice('ใช้ยาทะลวงขั้นแล้ว — ท้าผู้พิทักษ์ด่านที่ลานประลองเพื่อทะลวงขั้น');
    this.h.emit({ type: 'progressChanged' });
  }

  /** The challenge of the guardian is the trial: it needs the pill used and no cool-down. */
  refuse(cmd: PlayerCommand): boolean {
    if (cmd.t !== 'duelChallenge' || !this.held()) return false;
    const e = this.h.world.get(cmd.id);
    if (e?.sparTier !== this.guardianTier()) return false;
    const s = this.st;
    if (!s.open) {
      this.h.notice(`${HELD_NOTICE} ก่อนท้าผู้พิทักษ์`);
      return true;
    }
    if (s.cool > 0) {
      this.h.notice(`ลองใหม่ได้อีก ${Math.ceil(s.cool / 60)} นาที`);
      return true;
    }
    return false;
  }

  step(dt: number): void {
    const w = this.h.world;
    const pr = w.progress;
    const s = this.st;
    if (s.cool > 0) s.cool = Math.max(0, s.cool - dt);
    this.persist();
    if (s.passed < REALMS.length - 1) {
      const cap = rankThreshold(STAGES * (s.passed + 1)) - 1;
      if (pr.cultivationSpent > cap) {
        s.bank += pr.cultivationSpent - cap;
        pr.cultivationSpent = cap;
      }
    }
    if (!this.told && this.held()) {
      this.told = true;
      this.h.notice(HELD_NOTICE);
    }
    const d = w.duel;
    if (d?.open?.spar === this.guardianTier() && d.phase === 'result' && this.seen !== d && this.held() && s.open) {
      this.seen = d;
      if (d.won) this.win();
      else {
        s.cool = BREAKTHROUGH.retrySeconds;
        this.h.notice('ทะลวงขั้นไม่สำเร็จ — ลองใหม่ได้อีก 10 นาที ยายังอยู่');
      }
    }
  }

  private win(): void {
    const pr = this.h.world.progress;
    const s = this.st;
    const id = this.pill();
    if (id && (pr.trades.goods[id] ?? 0) > 0) {
      pr.trades.goods[id]! -= 1;
      if (pr.trades.goods[id] === 0) delete pr.trades.goods[id];
    }
    s.passed += 1;
    s.open = false;
    pr.cultivationSpent += s.bank;
    s.bank = 0;
    this.told = false;
    this.h.notice(`ทะลวงขั้นสำเร็จ — ${rankFor(pr.cultivationSpent).name}`);
    this.h.emit({ type: 'progressChanged' });
  }
}
